import { put, call, select } from "redux-saga/effects";
import { Map } from "immutable";
import {
  fetchInstallations,
  fetchInstallation,
  updateInstallation,
  publishInsallationProject,
  revertInstallationBundle,
  deleteInstallation,
  updatePublishHistoryEntry,
  fetchKioskAppVersions
} from "api";
import * as CONST from "actions/action_constants";
import * as selectors from "./selectors";

export function* fetchInstallationsSaga(action) {
  try {
    const { response, error } = yield call(fetchInstallations, action.username);

    if (response) {
      const { list } = response;
      yield put({
        type: CONST.INSTALLATIONS_FETCH_SUCCESS,
        list
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATIONS_FETCH_ERROR, message });
  }
}

export function* fetchInstallationSaga(action) {
  try {
    const { response, error } = yield call(fetchInstallation, action.installationID, action.filters);

    if (response) {
      yield put({
        type: CONST.INSTALLATION_FETCH_SUCCESS,
        installationID: action.installationID,
        installationData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_FETCH_ERROR, message });
  }
}

export function* updateInstallationSaga(action) {
  try {
    const { installationID, saveType } = action;

    const installation = yield select(
      selectors.getInstallation,
      installationID
    );
    const { response, error } = yield call(
      updateInstallation,
      installationID,
      installation,
      saveType
    );

    if (response) {
      yield put({
        type: CONST.INSTALLATION_UPDATE_SUCCESS,
        installationData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_UPDATE_ERROR, message });
  }
}

export function* publishInstallationProjectSaga(action) {
  try {
    const { installationID } = action;

    // Get the installation to find the associated project
    const installation = yield select(selectors.getInstallation, installationID);
    if (!installation || !installation.projectID) {
      throw new Error("Installation not found or has no associated project");
    }

    // Get the project to determine its type
    const project = yield select(selectors.getProject, installation.projectID);
    if (!project) {
      throw new Error("Project not found");
    }

    // Get project types to determine if this is a story-based or collection-based project
    const projectTypes = yield select(selectors.getProjectTypes) || {};
    
    // Determine project type: if project.typeID matches story type ID, it's "story", otherwise "collection"
    const projectType = project.typeID && projectTypes.story && project.typeID === projectTypes.story._id
      ? "story"
      : "collection";

    const { response, error } = yield call(
      publishInsallationProject,
      installationID,
      projectType
    );

    if (response) {
      yield put({
        type: CONST.INSTALLATION_PUBLISH_SUCCESS,
        projectData: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_PUBLISH_ERROR, message });
  }
}

export function* revertInstallationSaga(action) {
  try {
    const { installationID, bundleID } = action;

    const { response, error } = yield call(
      revertInstallationBundle,
      installationID,
      bundleID
    );

    if (response) {
      yield put({
        type: CONST.INSTALLATION_REVERT_SUCCESS,
        installationID
      });
      yield put({
        type: CONST.INSTALLATION_FETCH_REQUEST,
        installationID,
        filters: null
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_REVERT_ERROR, message });
  }
}

export function* deleteInstallationSaga(action) {
  try {
    const { selected: installationID } = action;

    const { response, error } = yield call(deleteInstallation, installationID);

    if (response) {
      yield put({ type: CONST.INSTALLATION_DELETE_SUCCESS, installationID });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_DELETE_ERROR, message });
  }
}

export function* updatePublishHistoryEntrySaga(action) {
  try {
    const { installationID, publishID, stage, status, metadata } = action;

    const { response, error } = yield call(
      updatePublishHistoryEntry,
      installationID,
      publishID,
      stage,
      status,
      metadata
    );

    if (response) {
      // After successful update, fetch the latest installation data
      yield put({
        type: CONST.INSTALLATION_FETCH_REQUEST,
        installationID,
        filters: null
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.INSTALLATION_UPDATE_ERROR, message });
  }
}

export function* fetchKioskAppVersionsSaga() {
  try {
    const { response, error } = yield call(fetchKioskAppVersions);

    if (response) {
      yield put({
        type: CONST.KIOSK_APP_VERSIONS_FETCH_SUCCESS,
        versions: response
      });
    } else {
      throw new Error(error);
    }
  } catch (error) {
    const { message } = error;
    yield put({ type: CONST.KIOSK_APP_VERSIONS_FETCH_ERROR, message });
  }
}
